import { Intervention } from '@seen/core/models/care-plans/intervention'
import { IconChevronDown, IconChevronUp, IconTrash } from '@tabler/icons-react'
import type { JSX, ReactNode } from 'react'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'
import { useDeleteMutation } from '#src/hooks/useDeleteMutation.ts'
import { useInterventionIsEditable } from '#src/hooks/useInterventionIsEditable.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'

interface InterventionActionsProps {
  intervention: Intervention
  interventions?: Intervention[]
  children: ReactNode
}

export function InterventionActions({
  intervention,
  interventions,
  children,
}: InterventionActionsProps): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const isEditable = useInterventionIsEditable(intervention)
  const open = useDeleteConfirmationStore((state) => state.open)
  const patchIntervention = usePatchMutation<Intervention>()
  const showLinkOverlay = usePortalStore((state) => state.showLinkOverlay)

  const deleteIntervention = useDeleteMutation(Intervention)

  if (isReadOnly || !isEditable) {
    return null
  }

  const index = interventions?.findIndex((i) => i.id === intervention.id) ?? -1
  const totalCount = interventions?.length ?? 0

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent>
        {showLinkOverlay && <DropdownMenuMedplumLinkItem to={intervention} />}
        {interventions && (
          <>
            <DropdownMenuItem
              disabled={index === 0}
              onClick={(e) => {
                e.stopPropagation()
                patchIntervention.mutate({
                  instance: intervention,
                  mutateFn: (instance) => instance.moveUp(interventions),
                })
              }}
            >
              <IconChevronUp className="size-4" />
              Move Up
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={index === totalCount - 1}
              onClick={(e) => {
                e.stopPropagation()
                patchIntervention.mutate({
                  instance: intervention,
                  mutateFn: (instance) => instance.moveDown(interventions),
                })
              }}
            >
              <IconChevronDown className="size-4" />
              Move Down
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation()
            open({
              title: 'Delete Intervention?',
              description:
                'Deleted resources can be recovered by contacting our team.',
              onConfirm: () => deleteIntervention.mutate(intervention),
            })
          }}
        >
          <IconTrash className="size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
